// om-fetch.ts — REMOTE RANGE-FETCH decode for Open-Meteo `.om` files.
//
// Decodes a SINGLE variable from a large (~100 MB+) remote `.om` file by
// fetching only the bytes that variable needs:
//   (1) the metadata tail  (trailer + root + node-tree + the var's LUT), and
//   (2) that variable's chunk-data byte block,
// instead of the whole file. This makes worldwide models (ICON global, etc.)
// practical over HTTP.
//
// ADDITIVE: builds on the in-memory clean-room reader in om-file.ts /
// om-decode.ts (bit-exact, typecheck-clean). It does NOT modify those files.
//
// STRATEGY (offset-faithful):
//   The in-memory reader (`readVariable`) works on a single Uint8Array whose
//   indices are ABSOLUTE file offsets (it scans for the variable name and
//   dereferences trailer.rootOffset, node.lutOffset, starts[ci], ... directly).
//   To reuse that logic unchanged we materialize a SPARSE full-size buffer:
//   one Uint8Array sized to the whole file, with ONLY the fetched windows
//   populated (everything else left zero). The metadata + chunk windows we
//   fetch are exactly the regions the reader touches for this variable, so the
//   decode is byte-identical to a whole-file decode — we just never download
//   (or read) the irrelevant bytes. `bytesFetched` reports the real network
//   cost (sum of Range responses), which is ≪ the allocated buffer / file size.
//
// File layout (low -> high):
//   [OM\x03][chunk data ...][per-var LUTs][node-tree][root][24B trailer]
//   trailer (last 24B) -> rootOffset / rootSize. Metadata + per-var LUTs sit
//   NEAR THE END; the target variable's chunk block sits earlier.

import { parseTrailer, readArrayNode, decodeLut, type OmArrayNode } from './om-file.js'
import { fullDecode, type ChunkMeta } from './om-decode.js'

// ---------------------------------------------------------------------------
// Tunables / options.
// ---------------------------------------------------------------------------

export interface ReadRemoteOptions {
  // Initial suffix-fetch size for the metadata tail (bytes). Default 2 MiB.
  // Sized to comfortably cover trailer + root + node-tree + (usually) the LUT.
  tailBytes?: number
  // Override fetch (for tests / non-browser). Defaults to global fetch.
  fetchImpl?: typeof fetch
  // Optional progress/debug logger.
  debug?: boolean
}

export interface RemoteVariableResult {
  dims: number[]
  chunk: number[]
  scale: number
  values: Int32Array // full flat field of raw (pre-scale) ints, identical to decode()
  bytesFetched: number // total bytes pulled over HTTP (the real cost)
  fileSize: number
  rangeRequests: number // number of HTTP Range requests issued
}

const DEFAULT_TAIL_BYTES = 2 * 1024 * 1024 // 2 MiB

// ---------------------------------------------------------------------------
// Low-level Range fetch. Returns the bytes AND (on a suffix request) the total
// file size parsed from the Content-Range header (`bytes start-end/total`).
// ---------------------------------------------------------------------------

interface RangeFetcher {
  fetchImpl: typeof fetch
  url: string
  bytesFetched: number
  rangeRequests: number
  debug: boolean
}

function parseContentRangeTotal(h: string | null): number | null {
  if (!h) return null
  // Format: "bytes 165446368-165446391/165446392"
  const slash = h.lastIndexOf('/')
  if (slash < 0) return null
  const totalStr = h.slice(slash + 1).trim()
  if (totalStr === '*') return null
  const n = Number(totalStr)
  return Number.isFinite(n) ? n : null
}

// Suffix fetch: last `n` bytes. Returns {bytes, total} (total from Content-Range).
async function fetchSuffix(
  f: RangeFetcher,
  n: number
): Promise<{ bytes: Uint8Array; total: number | null }> {
  const r = await f.fetchImpl(f.url, { headers: { Range: `bytes=-${n}` } })
  if (!(r.status === 206 || r.status === 200)) {
    throw new Error(`range fetch failed: HTTP ${r.status}`)
  }
  const total = parseContentRangeTotal(r.headers.get('content-range'))
  const ab = await r.arrayBuffer()
  const bytes = new Uint8Array(ab)
  // A bogus/empty 206 — e.g. the Capacitor local server "answering" an unservable
  // relative `/om/data_spatial` URL on native — yields no bytes / total 0. Treat it as
  // a failed fetch so the reader never derives degenerate offsets from empty metadata
  // (which would otherwise produce a malformed `bytes=X--N` range and crash the server).
  if (bytes.length === 0 || total === 0) {
    throw new Error(`om-fetch: empty suffix response (len=${bytes.length}, total=${total})`)
  }
  f.bytesFetched += bytes.length
  f.rangeRequests++
  if (f.debug) console.log(`[om-fetch] suffix ${n} -> ${bytes.length}B (total=${total})`)
  return { bytes, total }
}

// Standalone explicit byte-range read [start, end) (end exclusive). Returns the
// span bytes; chunk decoders use `start` as the buffer base to translate the
// absolute `starts[ci]` offsets. Explicit (positive) range -> CloudFront-cacheable.
export async function rangeRead(
  url: string,
  start: number,
  end: number,
  fetchImpl: typeof fetch = fetch
): Promise<Uint8Array> {
  const f: RangeFetcher = { fetchImpl, url, bytesFetched: 0, rangeRequests: 0, debug: false }
  return fetchRange(f, start, end)
}

// Absolute byte range [start, end) (end exclusive).
async function fetchRange(f: RangeFetcher, start: number, end: number): Promise<Uint8Array> {
  const last = end - 1
  // Never emit a malformed Range header. A non-positive span (end <= start) or a
  // negative end byte produces e.g. "bytes=0--1", whose empty middle segment crashes
  // Capacitor's local-server range parser (Integer.parseInt("")). Refuse it instead.
  if (!Number.isInteger(start) || !Number.isInteger(last) || start < 0 || last < start) {
    throw new Error(`om-fetch: refusing malformed range bytes=${start}-${last}`)
  }
  const r = await f.fetchImpl(f.url, { headers: { Range: `bytes=${start}-${last}` } })
  if (!(r.status === 206 || r.status === 200)) {
    throw new Error(`range fetch failed: HTTP ${r.status} for ${start}-${last}`)
  }
  const ab = await r.arrayBuffer()
  const bytes = new Uint8Array(ab)
  f.bytesFetched += bytes.length
  f.rangeRequests++
  if (f.debug) console.log(`[om-fetch] range ${start}-${last} -> ${bytes.length}B`)
  return bytes
}

// ---------------------------------------------------------------------------
// Sparse full-size buffer: one Uint8Array sized to the whole file, populated
// only in the windows we fetch. Reused as the `buf` for the in-memory reader,
// so all absolute-offset arithmetic (trailer.rootOffset, node.lutOffset,
// starts[ci], name scan) works exactly as on a whole-file buffer.
// ---------------------------------------------------------------------------

function makeView(buf: Uint8Array): DataView {
  return new DataView(buf.buffer, buf.byteOffset, buf.byteLength)
}

// ---------------------------------------------------------------------------
// Locate the variable's array node + dims/chunk inside the (sparse) tail buffer.
// Faithful re-implementation of om-file.ts's name-scan + backward dims/chunk
// walk, but bounded to the fetched tail window so we never read unfetched
// (zero) bytes during metadata resolution.
// ---------------------------------------------------------------------------

function indexOfBytes(hay: Uint8Array, needle: Uint8Array, start: number, end: number): number {
  const n = needle.length
  if (n === 0) return start
  const last = Math.min(end, hay.length) - n
  for (let i = start; i <= last; i++) {
    let k = 0
    while (k < n && hay[i + k] === needle[k]) k++
    if (k === n) return i
  }
  return -1
}

function asciiBytes(s: string): Uint8Array {
  const out = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff
  return out
}

function readU64(view: DataView, off: number): number {
  return Number(view.getBigUint64(off, true))
}

const chunkGrid = (dims: number[], chunk: number[]): number[] =>
  dims.map((d, i) => Math.ceil(d / chunk[i]))
const numChunks = (dims: number[], chunk: number[]): number =>
  chunkGrid(dims, chunk).reduce((a, b) => a * b, 1)

// ---------------------------------------------------------------------------
// LUT decode. The compressed chunk-offset LUT format is shared with the
// in-memory reader: blocks of 64 absolute offsets at a fixed even stride
// (lutSize / nBlocks), each a leading varbyte start + 64-bit TurboPFor delta
// block, decoded by om-file.ts's `decodeLut` (p4nddec64). We reuse that here and
// only add a structural self-check (monotonic + in-bounds), since the remote
// path may have an incompletely-fetched LUT window.
// ---------------------------------------------------------------------------

function isMonotonicInBounds(starts: number[], nC: number, lutOffset: number): boolean {
  if (!(starts[0] >= 3 && starts[0] <= lutOffset)) return false
  for (let i = 1; i <= nC; i++) {
    if (!(starts[i] >= starts[i - 1])) return false
    if (!(starts[i] <= lutOffset)) return false
  }
  return true
}

function decodeLutAuto(
  buf: Uint8Array,
  lutOffset: number,
  lutSize: number,
  nOffsets: number
): number[] {
  const nC = nOffsets - 1
  let starts: number[]
  try {
    starts = decodeLut(buf, lutOffset, lutSize, nOffsets)
  } catch {
    throw new Error('LUT decode failed (p4nddec64 threw)')
  }
  if (!isMonotonicInBounds(starts, nC, lutOffset)) {
    throw new Error('LUT decode failed: offset table not monotonic / in-bounds')
  }
  return starts
}

interface ResolvedVar {
  node: OmArrayNode
  dims: number[]
  chunk: number[]
}

// Read nDims dims[]+chunk[] immediately preceding a null-terminated name.
function readDimsChunk(
  view: DataView,
  nameOff: number,
  nDims: number
): { dims: number[]; chunk: number[]; dimsStart: number } {
  const chunkStart = nameOff - nDims * 8
  const dimsStart = chunkStart - nDims * 8
  const dims: number[] = []
  const chunk: number[] = []
  for (let i = 0; i < nDims; i++) {
    dims.push(readU64(view, dimsStart + i * 8))
    chunk.push(readU64(view, chunkStart + i * 8))
  }
  return { dims, chunk, dimsStart }
}

// Sanity gate on a candidate node + dims/chunk WITHOUT decoding the LUT (the LUT
// may not be in the tail window yet). Mirrors tryDecodeMeta's structural checks.
function sane(node: OmArrayNode, dims: number[], chunk: number[], fileSize: number): boolean {
  if (!(node.lutOffset > 3 && node.lutOffset < fileSize)) return false
  if (!(node.lutSize > 0 && node.lutSize < fileSize)) return false
  if (
    !Number.isFinite(node.scaleFactor) ||
    node.scaleFactor === 0 ||
    Math.abs(node.scaleFactor) > 1e12
  )
    return false
  if (dims.some(d => !(d > 0 && d < 1e8))) return false
  if (chunk.some((c, i) => !(c > 0 && c <= dims[i]))) return false
  const nC = numChunks(dims, chunk)
  if (!(nC > 0 && nC < 5e7)) return false
  return true
}

const isNameByte = (b: number): boolean =>
  (b >= 48 && b <= 57) || (b >= 65 && b <= 90) || (b >= 97 && b <= 122) || b === 95

// Find the variable node by scanning the fetched tail window for the name and
// walking back to its array node. `tailStart` is the absolute offset where the
// fetched tail begins (everything below it in `buf` is zero / unfetched).
function resolveVariable(
  buf: Uint8Array,
  view: DataView,
  varName: string,
  tailStart: number,
  fileSize: number
): ResolvedVar {
  const needle = asciiBytes(varName)
  let scan = tailStart
  while ((scan = indexOfBytes(buf, needle, scan, buf.length)) >= 0) {
    const after = buf[scan + needle.length]
    if (!isNameByte(after)) {
      for (let nDims = 1; nDims <= 6; nDims++) {
        const dc = readDimsChunk(view, scan, nDims)
        // node sits 56 bytes before dims block (matches om-file.ts).
        const nodeOff = dc.dimsStart - 56
        if (nodeOff < tailStart) continue // node not in fetched window
        const node = readArrayNode(view, nodeOff)
        if (sane(node, dc.dims, dc.chunk, fileSize)) {
          return { node, dims: dc.dims, chunk: dc.chunk }
        }
      }
    }
    scan += 1
  }
  throw new Error('could not resolve dims/chunk for variable: ' + varName)
}

// ---------------------------------------------------------------------------
// Explicit-range, allocation-light metadata resolution.
//
// CloudFront-cache friendly: uses ONLY positive byte ranges (suffix `bytes=-N`
// reads are the slow, poorly-cached path — confirmed ~15s cold vs ~0.35s for an
// explicit range), and never allocates a full-file buffer (the tail is indexed
// tail-relative). Returns the decode metadata (dims/chunk/grid/scale + absolute
// chunk-start offsets); callers cache it per (model,refTime,validTime,variable)
// and reuse it across every viewport read.
// ---------------------------------------------------------------------------

export interface VariableMeta {
  dims: number[]
  chunk: number[]
  grid: number[]
  scale: number
  starts: number[] // absolute file byte offsets, length numChunks+1
  fileSize: number
  bytesFetched: number
  rangeRequests: number
}

const DEFAULT_META_TAIL = 256 * 1024 // 256 KiB — covers trailer + node-tree + LUT for our grids

// File size via HEAD (Content-Length), falling back to a 1-byte positive range
// (Content-Range total). Both are tiny and edge-cacheable.
async function fetchFileSize(f: RangeFetcher): Promise<number> {
  try {
    const h = await f.fetchImpl(f.url, { method: 'HEAD' })
    f.rangeRequests++
    if (h.ok) {
      const len = Number(h.headers.get('content-length'))
      if (Number.isFinite(len) && len > 0) return len
    }
  } catch {
    /* fall through to a range probe */
  }
  const r = await f.fetchImpl(f.url, { headers: { Range: 'bytes=0-0' } })
  const total = parseContentRangeTotal(r.headers.get('content-range'))
  await r.arrayBuffer()
  f.bytesFetched += 1
  f.rangeRequests++
  if (total == null) throw new Error('om-fetch: could not determine file size')
  return total
}

// Tail-relative variant of resolveVariable: scans the small tail buffer whose
// byte 0 is absolute offset `tailStart`. dims/chunk and node fields are read at
// tail-relative offsets; node.lutOffset is the stored ABSOLUTE offset.
function resolveVariableTail(
  tail: Uint8Array,
  view: DataView,
  varName: string,
  fileSize: number
): ResolvedVar {
  const needle = asciiBytes(varName)
  let scan = 0
  while ((scan = indexOfBytes(tail, needle, scan, tail.length)) >= 0) {
    const after = tail[scan + needle.length]
    if (!isNameByte(after)) {
      for (let nDims = 1; nDims <= 6; nDims++) {
        const dc = readDimsChunk(view, scan, nDims)
        const nodeOff = dc.dimsStart - 56
        if (nodeOff < 0) continue // node sits below the fetched tail window
        const node = readArrayNode(view, nodeOff)
        if (sane(node, dc.dims, dc.chunk, fileSize)) {
          return { node, dims: dc.dims, chunk: dc.chunk }
        }
      }
    }
    scan += 1
  }
  throw new Error('could not resolve dims/chunk for variable: ' + varName)
}

export async function resolveVariableMeta(
  url: string,
  varName: string,
  opts: ReadRemoteOptions = {}
): Promise<VariableMeta> {
  const fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis)
  const f: RangeFetcher = {
    fetchImpl,
    url,
    bytesFetched: 0,
    rangeRequests: 0,
    debug: opts.debug ?? false,
  }

  const fileSize = await fetchFileSize(f)
  const tailLen = Math.min(opts.tailBytes ?? DEFAULT_META_TAIL, fileSize)
  let tailStart = fileSize - tailLen
  let tail = await fetchRange(f, tailStart, fileSize) // explicit [tailStart, fileSize)
  let view = makeView(tail)

  // Trailer = last 24 bytes of the tail (== last 24 of the file).
  const tr = parseTrailer(tail, view)

  // Ensure the metadata root (and thus the node-tree we scan) is inside the
  // tail; extend the window down to rootOffset if not. Trailer stays at the end.
  if (tr.rootOffset < tailStart) {
    tailStart = tr.rootOffset
    tail = await fetchRange(f, tailStart, fileSize)
    view = makeView(tail)
  }

  const resolved = resolveVariableTail(tail, view, varName, fileSize)

  // Ensure the LUT bytes are present; extend the tail down to the LUT if needed.
  if (resolved.node.lutOffset < tailStart) {
    tailStart = resolved.node.lutOffset
    tail = await fetchRange(f, tailStart, fileSize)
    view = makeView(tail)
  }

  const dims = resolved.dims
  const chunk = resolved.chunk
  const grid = chunkGrid(dims, chunk)
  const nC = numChunks(dims, chunk)
  // decodeLut reads bytes at (lutOffset - tailStart) within the tail; the decoded
  // values are ABSOLUTE chunk byte offsets, so validate against the absolute
  // lutOffset (chunk data lives before the LUT in the file).
  const starts = decodeLut(tail, resolved.node.lutOffset - tailStart, resolved.node.lutSize, nC + 1)
  if (!isMonotonicInBounds(starts, nC, resolved.node.lutOffset)) {
    throw new Error('om-fetch: LUT decode failed (offsets not monotonic / in-bounds)')
  }

  return {
    dims,
    chunk,
    grid,
    scale: resolved.node.scaleFactor,
    starts,
    fileSize,
    bytesFetched: f.bytesFetched,
    rangeRequests: f.rangeRequests,
  }
}

// ---------------------------------------------------------------------------
// Public API.
// ---------------------------------------------------------------------------

export async function readVariableRemote(
  url: string,
  varName: string,
  opts: ReadRemoteOptions = {}
): Promise<RemoteVariableResult> {
  // NOTE: must bind to globalThis. Calling the global `fetch` through an alias
  // (`const f = fetch; f(...)`) throws "TypeError: Illegal invocation" in browsers
  // because `this` is no longer the global object. Node tolerates it, which is why
  // this only failed in the live app, not in node tests.
  const fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis)
  const debug = opts.debug ?? false
  const tailBytes = opts.tailBytes ?? DEFAULT_TAIL_BYTES

  const f: RangeFetcher = { fetchImpl, url, bytesFetched: 0, rangeRequests: 0, debug }

  // (1) Suffix-fetch the tail to cover trailer + root + node-tree (+ usually LUT).
  const suffix = await fetchSuffix(f, tailBytes)
  let fileSize = suffix.total
  if (fileSize == null) {
    // Fallback: a tiny suffix request just to read Content-Range total.
    const tiny = await fetchSuffix(f, 4)
    fileSize = tiny.total
    if (fileSize == null) throw new Error('could not determine file size (no Content-Range)')
  }

  // Sparse full-size buffer; place the tail at its absolute offset.
  const buf = new Uint8Array(fileSize)
  let tailStart = fileSize - suffix.bytes.length
  buf.set(suffix.bytes, tailStart)
  let view = makeView(buf)

  // Parse trailer (last 24 bytes — always inside the tail).
  const tr = parseTrailer(buf, view)

  // (1b) Ensure root + node-tree are inside the fetched window. If rootOffset
  // is below tailStart, extend the tail down to cover [rootOffset, fileSize).
  if (tr.rootOffset < tailStart) {
    const extra = await fetchRange(f, tr.rootOffset, tailStart)
    buf.set(extra, tr.rootOffset)
    tailStart = tr.rootOffset
    view = makeView(buf)
  }

  // (1c) Locate the variable's array node + dims/chunk.
  const resolved = resolveVariable(buf, view, varName, tailStart, fileSize)
  const { node } = resolved
  const dims = resolved.dims
  const chunk = resolved.chunk
  const grid = chunkGrid(dims, chunk)
  const nC = numChunks(dims, chunk)

  // (2) Ensure the LUT bytes [lutOffset, lutOffset+lutSize) are present, then
  // decode -> `starts` (nC+1 absolute chunk-start offsets).
  const lutStart = node.lutOffset
  if (lutStart < tailStart) {
    // LUT is below the tail window; fetch exactly the LUT block (down to its
    // start), then extend tailStart so subsequent reads stay in-window.
    const extra = await fetchRange(f, lutStart, tailStart)
    buf.set(extra, lutStart)
    tailStart = lutStart
    view = makeView(buf)
  }
  const starts = decodeLutAuto(buf, node.lutOffset, node.lutSize, nC + 1)

  // (3) Range-fetch the variable's chunk-data block [starts[0], starts[nC]).
  // This is contiguous in the file, so it's a SINGLE coalesced Range request.
  const blockStart = starts[0]
  const blockEnd = starts[nC]
  if (blockStart < tailStart && blockEnd > blockStart) {
    const need = Math.min(blockEnd, tailStart) // upper part may already be in-window
    const data = await fetchRange(f, blockStart, need)
    buf.set(data, blockStart)
    view = makeView(buf)
  }

  // (4) Decode -> full flat Int32Array, reusing the existing chunk pipeline.
  const meta: ChunkMeta = { dims, chunk, grid, starts }
  const values = fullDecode(meta, buf)

  // (5) Result.
  return {
    dims,
    chunk,
    scale: node.scaleFactor,
    values,
    bytesFetched: f.bytesFetched,
    fileSize,
    rangeRequests: f.rangeRequests,
  }
}

// ---------------------------------------------------------------------------
// URL helper: build a current ICON Global `.om` URL from latest.json.
// Path form: dwd_icon/YYYY/MM/DD/HH00Z/YYYY-MM-DDTHH00.om
// ---------------------------------------------------------------------------

export async function latestIconGlobalUrl(
  validTimeIndex = 1,
  fetchImpl: typeof fetch = fetch
): Promise<string> {
  const base = 'https://openmeteo.s3.us-west-2.amazonaws.com/data_spatial/dwd_icon'
  const r = await fetchImpl(`${base}/latest.json`)
  if (!r.ok) throw new Error(`latest.json fetch failed: HTTP ${r.status}`)
  const j = (await r.json()) as { reference_time: string; valid_times: string[] }
  const ref = j.reference_time // e.g. "2026-05-29T12:00:00Z"
  const valid = j.valid_times[Math.min(validTimeIndex, j.valid_times.length - 1)]
  // reference time -> directory: YYYY/MM/DD/HH00Z
  const rd = new Date(ref)
  const Y = rd.getUTCFullYear()
  const M = String(rd.getUTCMonth() + 1).padStart(2, '0')
  const D = String(rd.getUTCDate()).padStart(2, '0')
  const H = String(rd.getUTCHours()).padStart(2, '0')
  const dir = `${Y}/${M}/${D}/${H}00Z`
  // valid time -> file: YYYY-MM-DDTHH00.om  (valid is like "2026-05-29T13:00Z")
  const vd = new Date(valid)
  const vY = vd.getUTCFullYear()
  const vM = String(vd.getUTCMonth() + 1).padStart(2, '0')
  const vD = String(vd.getUTCDate()).padStart(2, '0')
  const vH = String(vd.getUTCHours()).padStart(2, '0')
  const file = `${vY}-${vM}-${vD}T${vH}00.om`
  return `${base}/${dir}/${file}`
}
